import { RefObject, useCallback, useMemo, useState } from 'react'
import { GridApiPremium, GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { useFlags } from '@equipmentshare/feature-flags'
import { FulfillmentOrderRow } from '../hooks/useFulfillmentOrdersDataQuery'
import { useDcAdminContext } from '../contexts/DcAdminContext'
import useCanCreatePickOrder from './useCanCreatePickOrder'
import {
	anchorDestinationKey,
	canSelectForPickOrder,
	idsFromSelectionModel,
	isPickableFulfillmentOrder,
} from './selection'

/**
 * A fulfillment order the user has checked, remembered as they check it.
 *
 * The grid is paged from the server, so a row selected on one page is gone from
 * the row model by the time another page loads. Capturing what is needed at the
 * moment of selection is what lets a run span pages.
 */
export type SelectedPickOrderRow = {
	id: string,
	orderNumber: number,
	destinationKey: string,
	destinationDescription: string | null,
}

function toSelectedRow( row: FulfillmentOrderRow ): SelectedPickOrderRow {
	return {
		id: row.id,
		orderNumber: Number( row.orderNumber ),
		destinationKey: row.destinationKey,
		destinationDescription: row.destinationDescription ?? null,
	}
}

/**
 * Choosing the fulfillment orders a pick order will cover, from the DC Admin
 * fulfillment orders table.
 *
 * The first order checked anchors the run: every other order offered has to
 * share its destination. That comparison is `destinationKey`, which inventory-api
 * renders from the same function its creation invariant enforces, so the table
 * can group orders itself — synchronously, with no round trip, and with no
 * ability to disagree about what "the same place" means.
 */
export default function useFulfillmentOrderPickSelection( apiRef: RefObject<GridApiPremium | null> ) {
	const flags = useFlags()
	const { selectedDcId } = useDcAdminContext()
	// The table is already scoped to the DC in the header, so that is the DC a
	// run started here belongs to.
	const canCreate = useCanCreatePickOrder( selectedDcId )
	const enabled = Boolean( flags.costentoryEnablePickOrders ) && canCreate

	const [ selected, setSelected ] = useState<SelectedPickOrderRow[]>( [] )

	const selectedIds = useMemo( () => new Set( selected.map( ( row ) => row.id ) ), [ selected ] )
	const anchorKey = anchorDestinationKey( selected )

	const selectionModel: GridRowSelectionModel = useMemo(
		() => ( { type: 'include', ids: new Set<string>( selected.map( ( row ) => row.id ) ) } ),
		[ selected ],
	)

	const isRowSelectable = useCallback(
		( params: { row: FulfillmentOrderRow } ) => canSelectForPickOrder( params.row, selectedIds, anchorKey ),
		[ selectedIds, anchorKey ],
	)

	const onSelectionModelChange = useCallback( ( model: GridRowSelectionModel ) => {
		const loadedIds = ( apiRef.current?.getAllRowIds() ?? [] ).map( String )
		const ids = idsFromSelectionModel( model, loadedIds )
		const idSet = new Set( ids )

		setSelected( ( prev ) => {
			// Anchor order is what the destination constraint hangs on, so surviving
			// rows keep their positions and newly checked ones go on the end.
			const kept = prev.filter( ( row ) => idSet.has( row.id ) )
			const keptIds = new Set( kept.map( ( row ) => row.id ) )
			const added = ids
				.filter( ( id ) => !keptIds.has( id ) )
				.map( ( id ) => apiRef.current?.getRow( id ) as FulfillmentOrderRow | undefined )
				.filter( ( row ): row is FulfillmentOrderRow => !!row && isPickableFulfillmentOrder( row ) )
				.map( toSelectedRow )
			return [ ...kept, ...added ]
		} )
	}, [ apiRef ] )

	const clear = useCallback( () => setSelected( [] ), [] )

	return {
		enabled,
		fulfillingInventoryId: selectedDcId,
		selected,
		selectionModel,
		isRowSelectable,
		onSelectionModelChange,
		clear,
	}
}
